import React, { useMemo, useRef, useState } from 'react';
import api from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Upload, GitCompare, Send, CheckCircle2, XCircle, Rocket, Trash2 } from 'lucide-react';
import { toast } from 'sonner';

const STATUS_STYLE = {
  DRAFT: 'border-slate-300 text-slate-700 bg-slate-50',
  REVIEW: 'border-amber-300 text-amber-700 bg-amber-50',
  APPROVED: 'border-blue-300 text-blue-700 bg-blue-50',
  PUBLISHED: 'border-green-300 text-green-700 bg-green-50',
  DISCARDED: 'border-red-200 text-red-600 bg-red-50',
};
const errMsg = (e, fallback) => e.response?.data?.detail?.error?.message || e.response?.data?.detail?.message || fallback;

export const VersionLifecycleCard = ({ versions, canPublish, userId, onChanged }) => {
  const fileRef = useRef(null);
  const [busy, setBusy] = useState(null);
  const [compare, setCompare] = useState(null);
  const lifecycle = useMemo(() => versions.filter((v) => v.status !== 'PUBLISHED'), [versions]);

  const act = async (label, fn) => {
    setBusy(label);
    try { await fn(); toast.success(`${label} complete`); onChanged?.(); }
    catch (e) { toast.error(errMsg(e, `${label} failed`)); }
    finally { setBusy(null); }
  };
  const upload = (file) => act('Upload draft', async () => {
    const form = new FormData(); form.append('file', file);
    await api.post('/ndis-catalogue/drafts', form, { headers: { 'Content-Type': 'multipart/form-data' } });
  });
  const showCompare = (v) => act('Compare', async () => { const r = await api.get(`/ndis-catalogue/versions/${v.id}/impact`); setCompare(r.data.data); });
  const reject = (v) => { const reason = window.prompt('Rejection reason'); if (reason) act('Reject', () => api.post(`/ndis-catalogue/versions/${v.id}/reject`, { reason })); };

  return (
    <Card data-testid="ndis-version-lifecycle-card">
      <CardContent className="py-4 space-y-3">
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <div>
            <p className="font-medium">Reference version lifecycle</p>
            <p className="text-xs text-muted-foreground">Upload → Draft → Compare → Impact → Review → Maker-checker approval → Publish. Published versions are immutable.</p>
          </div>
          <input ref={fileRef} type="file" accept=".xlsx" className="hidden" data-testid="ndis-draft-file-input" onChange={(e) => e.target.files?.[0] && upload(e.target.files[0])} />
          <Button size="sm" variant="outline" disabled={!!busy} onClick={() => fileRef.current?.click()} data-testid="ndis-upload-draft-button"><Upload className="h-4 w-4 mr-2" />Upload source (.xlsx)</Button>
        </div>
        {lifecycle.length === 0 ? (
          <p className="text-sm text-muted-foreground" data-testid="ndis-lifecycle-empty">No draft versions. Upload a new NDIS workbook to start a review.</p>
        ) : (
          <Table data-testid="ndis-lifecycle-table">
            <TableHeader><TableRow><TableHead>Version</TableHead><TableHead>Status</TableHead><TableHead>Items</TableHead><TableHead>Uploaded by</TableHead><TableHead className="text-right">Actions</TableHead></TableRow></TableHeader>
            <TableBody>
              {lifecycle.map((v) => {
                const isMaker = v.imported_by === userId;
                return (
                  <TableRow key={v.id} data-testid={`ndis-lifecycle-row-${v.id}`}>
                    <TableCell className="font-medium">{v.version_label}</TableCell>
                    <TableCell><Badge variant="outline" className={STATUS_STYLE[v.status] || ''} data-testid={`ndis-version-status-${v.id}`}>{v.status}</Badge></TableCell>
                    <TableCell>{v.counts?.total ?? '—'}</TableCell>
                    <TableCell className="text-xs">{isMaker ? 'you (maker)' : v.imported_by?.slice(-6)}</TableCell>
                    <TableCell className="text-right space-x-1">
                      <Button size="sm" variant="ghost" disabled={!!busy} onClick={() => showCompare(v)} data-testid={`ndis-compare-${v.id}`}><GitCompare className="h-4 w-4" /></Button>
                      {v.status === 'DRAFT' && <Button size="sm" variant="ghost" disabled={!!busy} onClick={() => act('Submit for review', () => api.post(`/ndis-catalogue/versions/${v.id}/submit`))} data-testid={`ndis-submit-${v.id}`}><Send className="h-4 w-4" /></Button>}
                      {v.status === 'DRAFT' && <Button size="sm" variant="ghost" disabled={!!busy} onClick={() => act('Discard', () => api.post(`/ndis-catalogue/versions/${v.id}/discard`))} data-testid={`ndis-discard-${v.id}`}><Trash2 className="h-4 w-4" /></Button>}
                      {v.status === 'REVIEW' && canPublish && <Button size="sm" variant="ghost" disabled={!!busy || isMaker} title={isMaker ? 'Maker cannot approve own upload' : 'Approve'} onClick={() => act('Approve', () => api.post(`/ndis-catalogue/versions/${v.id}/approve`, {}))} data-testid={`ndis-approve-${v.id}`}><CheckCircle2 className="h-4 w-4" /></Button>}
                      {v.status === 'REVIEW' && canPublish && <Button size="sm" variant="ghost" disabled={!!busy} onClick={() => reject(v)} data-testid={`ndis-reject-${v.id}`}><XCircle className="h-4 w-4" /></Button>}
                      {v.status === 'APPROVED' && canPublish && <Button size="sm" disabled={!!busy || isMaker} title={isMaker ? 'Maker cannot publish own upload' : 'Publish'} onClick={() => act('Publish', () => api.post(`/ndis-catalogue/versions/${v.id}/publish`, {}))} data-testid={`ndis-publish-${v.id}`}><Rocket className="h-4 w-4 mr-1" />Publish</Button>}
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        )}
        {compare && (
          <div className="rounded-md border p-3 text-sm space-y-1" data-testid="ndis-compare-panel">
            <div className="flex items-center justify-between"><p className="font-medium">Compare vs {compare.previous_version_label || 'no published version'}</p><Button size="sm" variant="ghost" onClick={() => setCompare(null)} data-testid="ndis-compare-close">Close</Button></div>
            <p>New <b data-testid="ndis-compare-new">{compare.counts.new}</b> · Changed <b data-testid="ndis-compare-changed">{compare.counts.changed}</b> · Expired <b data-testid="ndis-compare-expired">{compare.counts.expired}</b> · Unchanged {compare.counts.unchanged}</p>
            <p className="text-muted-foreground">Impact: {compare.impact.branch_catalogue_items_enabled} enabled branch items, {compare.impact.agreements_with_affected_lines} agreements with affected lines, {compare.impact.agreed_rate_above_new_cap.length} agreed rates above new cap.</p>
          </div>
        )}
      </CardContent>
    </Card>
  );
};
